import { Schema } from "effect";
import { MongoCallRecordId } from "../../../../ids/mongo-ids";
import { MessageEmailType } from "./email";
import {
  MessageMetaCommentAttribute,
  MessageMetaCommentState,
  MessageMetaCommentType,
} from "./meta-comment";
import { MessageRedoChatConfirmationState } from "./redo-chat";
import { MessageSmsKind, MessageSmsService } from "./sms";

// Email channel data
export const EmailChannelData = Schema.Struct({
  _tag: Schema.Literal("email"),
  type: MessageEmailType,
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * URL to the HTML body content of the email message.
   * If there's content in the body, this should always exist.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Parsed, sanitized HTML of the email message, stripping
   * out the reply quotations.
   */
  parsedHtmlBody: Schema.NullOr(
    Schema.String.pipe(Schema.maxLength(16 * 1024)),
  ),
});
export type EmailChannelData = typeof EmailChannelData.Type;

// SMS channel data
export const SmsChannelData = Schema.Struct({
  _tag: Schema.Literal("sms"),
  kind: MessageSmsKind,
  messageService: MessageSmsService,
});
export type SmsChannelData = typeof SmsChannelData.Type;

// Redo Chat channel data
export const RedoChatChannelData = Schema.Struct({
  _tag: Schema.Literal("redoChat"),
  contentType: Schema.String,
  structuredContent: Schema.NullOr(Schema.Unknown),
  confirmationState: Schema.NullOr(MessageRedoChatConfirmationState),
});
export type RedoChatChannelData = typeof RedoChatChannelData.Type;

// Voice channel data
export const VoiceChannelData = Schema.Struct({
  _tag: Schema.Literal("voice"),
  mongoCallRecordId: Schema.NullOr(MongoCallRecordId),
});
export type VoiceChannelData = typeof VoiceChannelData.Type;

// Meta comment channel data (for facebook and instagram DMs)
export const MetaCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("metaComment"),
  type: MessageMetaCommentType,
  state: Schema.NullOr(MessageMetaCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageMetaCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type MetaCommentChannelData = typeof MetaCommentChannelData.Type;

// Discriminated union of all message channel data types
export const MessageChannelData = Schema.Union(
  EmailChannelData,
  SmsChannelData,
  RedoChatChannelData,
  VoiceChannelData,
  MetaCommentChannelData,
);
export type MessageChannelData = typeof MessageChannelData.Type;
